import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge, Button, Checkbox, InputNumber, Space, Table, Typography, message } from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import MerchantSelect from '@components/operator/MerchantSelect';
import Can from '@shared/permissions/Can';
import ListPageHeader from '@shared/ListPageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';

import { replenishmentService } from '@services/operator/replenishmentService';
import type {
  PreviewEntry,
  PreviewSplitSummary,
  ReplenishmentNeed,
  SlotKey,
} from '@services/operator/replenishmentService';
import PreviewModal from './components/PreviewModal';

const { Text } = Typography;

// 揀補儲位唯一鍵 (product, inventory_type, shelf) 序列化成 row key，供 rowSelection 用。
// 後端 slot 的 inventory_type_id 可能為 null（slot 未綁定庫存類型），
// 序列化時以空字串代替；還原時再轉回 null 對齊 SlotKey。
const needRowKey = (n: ReplenishmentNeed): string =>
  `${n.product_id}|${n.inventory_type_id ?? ''}|${n.shelf_id}`;

// 補貨清單是「建立單一貨主補貨單」的入口（needs → preview → generate；後端 generate /
// preview 強制單一 merchant_id）。補貨屬 Enterprise（單一 merchant 自動鎖定 effect 不會跑），
// 且系統沒有常駐 header 貨主切換器 —— 唯一的貨主切換 UI 就是 <MerchantSelect> 自帶的 bar。
// 故比照 StockTransferOrders/New 由本頁的 <MerchantSelect> 建立 merchant context：未選貨主時
// 顯示貨主下拉（而非死路空狀態），選定後頂部顯示「目前貨主 ｜ 更換」bar，render-prop 把
// 已鎖定的 merchantId 交給 Body —— Body 內所有查詢/操作都保證帶到該貨主。
function ReplenishmentNeeds() {
  return (
    <MerchantSelect>
      {({ merchantId }: { merchantId: string }) => (
        <ReplenishmentNeedsBody merchantId={merchantId} />
      )}
    </MerchantSelect>
  );
}

function ReplenishmentNeedsBody({ merchantId }: { merchantId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // 拆分設定：依區域分群預設開；每單明細上限留空 = 不限。
  const [groupByZone, setGroupByZone] = useState(true);
  const [maxItems, setMaxItems] = useState<number | null>(null);
  const [previewSplit, setPreviewSplit] = useState<PreviewSplitSummary[]>([]);

  const [needs, setNeeds] = useState<ReplenishmentNeed[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewEntries, setPreviewEntries] = useState<PreviewEntry[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  // merchantId 由 <MerchantSelect> render-prop 保證為已選定貨主（未選時根本不會 render 本 Body）。
  const loadNeeds = useCallback(async () => {
    setLoading(true);
    try {
      const res = await replenishmentService.getNeeds({
        merchant_id: merchantId,
        include_wave_demand: true,
      });
      if (res.success) {
        setNeeds(res.data);
      } else {
        handleApiError(res, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.load_failed') });
    } finally {
      setLoading(false);
    }
  }, [merchantId, t]);

  // 掛載 + merchant 變更時載入需求；切 merchant 後既有選取已無意義，一併清空。
  useEffect(() => {
    setSelectedKeys([]);
    loadNeeds();
  }, [loadNeeds]);

  // 將選取的 row key 還原成後端要的 SlotKey[]（inventory_type_id 維持 string | null）。
  const selectedSlots = useCallback((): SlotKey[] => {
    const keySet = new Set(selectedKeys.map(String));
    return needs
      .filter((n) => keySet.has(needRowKey(n)))
      .map((n) => ({
        product_id: n.product_id,
        inventory_type_id: n.inventory_type_id ?? null,
        shelf_id: n.shelf_id,
      }));
  }, [needs, selectedKeys]);

  // 預覽為 dry-run：只算 FEFO 來源計畫 + shortage，不寫 DB（產生在 modal 確認時才發生）。
  const handlePreview = async () => {
    const slots = selectedSlots();
    if (slots.length === 0) return;
    setPreviewLoading(true);
    try {
      const res = await replenishmentService.previewOrder({
        merchant_id: merchantId,
        slots,
        include_wave_demand: true,
        group_by_zone: groupByZone,
        ...(maxItems ? { max_items: maxItems } : {}),
      });
      if (res.success) {
        setPreviewEntries(res.data.entries);
        setPreviewSplit(res.data.split);
        setPreviewOpen(true);
      } else {
        handleApiError(res, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.load_failed') });
    } finally {
      setPreviewLoading(false);
    }
  };

  // 產生 (persist)：以同一組 slots + 拆分設定建立補貨單（可能產生多張）；
  // 成功後重置選取並導向補貨單列表（不再 inline 重載需求）。
  const handleGenerate = async () => {
    const slots = selectedSlots();
    if (slots.length === 0) return;
    setGenerating(true);
    try {
      const res = await replenishmentService.generateOrder({
        merchant_id: merchantId,
        slots,
        include_wave_demand: true,
        group_by_zone: groupByZone,
        ...(maxItems ? { max_items: maxItems } : {}),
      });
      if (res.success) {
        message.success(t('operator.replenishment.needs.split.generated_count', { count: res.data.length }));
        setPreviewOpen(false);
        setPreviewEntries([]);
        setPreviewSplit([]);
        setSelectedKeys([]);
        navigate('/replenishment_orders');
      } else {
        handleApiError(res, { t, fallbackMessage: t('common.error.create_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t, fallbackMessage: t('common.error.create_failed') });
    } finally {
      setGenerating(false);
    }
  };

  const columns = useMemo<ColumnsType<ReplenishmentNeed>>(
    () => [
      {
        title: t('operator.replenishment.needs.columns.product'),
        key: 'product',
        // 商品 cell 比照同 feature 的 Show.tsx：名稱 + SKU 灰字堆疊，整體連結到商品詳情頁
        // （保留 Cmd/中鍵開新分頁的原生行為）。product_name 可能為 null → 以 '-' 墊底。
        render: (_: unknown, row: ReplenishmentNeed) => (
          <Link to={`/products/${row.product_id}`}>
            <div>{row.product_name ?? '-'}</div>
            {row.product_sku && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.product_sku}
              </Text>
            )}
          </Link>
        ),
      },
      {
        title: t('operator.replenishment.needs.columns.inventory_type'),
        dataIndex: 'inventory_type_name',
        key: 'inventory_type_name',
        render: (name: string | null) => name ?? '-',
      },
      {
        title: t('operator.replenishment.needs.columns.shelf'),
        dataIndex: 'shelf_location',
        key: 'shelf_location',
        render: (loc: string | null) => loc ?? '-',
      },
      {
        title: t('operator.replenishment.needs.columns.on_face'),
        dataIndex: 'on_face',
        key: 'on_face',
      },
      {
        title: t('operator.replenishment.needs.columns.min'),
        dataIndex: 'min',
        key: 'min',
      },
      {
        title: t('operator.replenishment.needs.columns.capacity'),
        dataIndex: 'capacity',
        key: 'capacity',
      },
      {
        title: t('operator.replenishment.needs.columns.wave_demand'),
        dataIndex: 'wave_demand',
        key: 'wave_demand',
      },
      {
        title: t('operator.replenishment.needs.columns.need'),
        key: 'need',
        render: (_: unknown, row: ReplenishmentNeed) => (
          <Space size={4}>
            <span>{row.need}</span>
            {row.over_capacity && (
              <Badge
                status="warning"
                text={t('operator.replenishment.needs.over_capacity')}
              />
            )}
          </Space>
        ),
      },
    ],
    [t],
  );

  const rowSelection: TableRowSelection<ReplenishmentNeed> = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys),
  };

  return (
    <div>
      <ListPageHeader
        items={['replenishment', 'replenishment.needs']}
        actions={
          <Can requiredPermission="inventories:write">
            <Button
              type="primary"
              disabled={selectedKeys.length === 0}
              loading={previewLoading}
              onClick={handlePreview}
            >
              {t('operator.replenishment.needs.preview')}
            </Button>
          </Can>
        }
      />
      <Space style={{ marginBottom: 16 }} size="large" wrap>
        <Checkbox checked={groupByZone} onChange={(e) => setGroupByZone(e.target.checked)}>
          {t('operator.replenishment.needs.split.group_by_zone')}
        </Checkbox>
        <Space size={4}>
          <Text>{t('operator.replenishment.needs.split.max_items')}</Text>
          <InputNumber
            min={1}
            value={maxItems}
            onChange={(v) => setMaxItems(typeof v === 'number' ? v : null)}
            placeholder={t('common.unlimited')}
          />
        </Space>
      </Space>
      <Table<ReplenishmentNeed>
        rowKey={needRowKey}
        loading={loading}
        pagination={false}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={needs}
      />
      <PreviewModal
        open={previewOpen}
        entries={previewEntries}
        split={previewSplit}
        confirmLoading={generating}
        onConfirm={handleGenerate}
        onCancel={() => setPreviewOpen(false)}
      />
    </div>
  );
}

export default ReplenishmentNeeds;
